import React, { useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Lock, 
  Zap, 
  Globe, 
  ArrowRight, 
  Menu, 
  X,
  CheckCircle,
  Star,
  Users,
  Award,
  Scroll,
  UserCheck,
  Clock,
  FileText,
  Code,
  Terminal,
  Webhook,
  Key,
  Database,
  Layers,
  ExternalLink,
  GraduationCap,
  Heart,
  Building2,
  BookOpen,
  Home,
  Briefcase
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import Button from '../components/atoms/Button';
import Card from '../components/atoms/Card';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activePersona, setActivePersona] = useState('students');
  const { scrollY } = useScroll();
  const headerOpacity = useTransform(scrollY, [0, 100], [0.95, 0.98]);
  const heroY = useTransform(scrollY, [0, 300], [0, -50]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const features = [
    {
      icon: Shield,
      title: 'Military-Grade Security',
      description: 'End-to-end encryption with AES-256 and zero-knowledge architecture ensures your documents remain completely private.',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Lock,
      title: 'Blockchain Notarization',
      description: 'Immutable proof of authenticity using blockchain technology. Verify document integrity with cryptographic certainty.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Scroll,
      title: 'Digital Will & Witness',
      description: 'Create secure digital wills with cryptographic witness verification. Ensure your legacy is protected with blockchain-backed inheritance.',
      gradient: 'from-emerald-500 to-teal-500'
    },
    {
      icon: UserCheck,
      title: 'Witness Network',
      description: 'Establish trusted witness networks with multi-signature verification. Automated wellness checks and secure execution protocols.',
      gradient: 'from-indigo-500 to-purple-500'
    },
    {
      icon: Zap,
      title: 'Lightning Fast Access',
      description: 'Instant uploads, real-time sync, and lightning-fast retrieval. Access your documents anywhere, anytime.',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: Globe,
      title: 'Global Accessibility',
      description: 'Secure access from any device, anywhere in the world. Your digital vault follows you wherever you go.',
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  const stats = [
    { icon: Users, value: '50K+', label: 'Trusted Users' },
    { icon: Shield, value: '99.9%', label: 'Uptime' },
    { icon: Award, value: '256-bit', label: 'Encryption' },
    { icon: Star, value: '4.9/5', label: 'User Rating' }
  ];

  const navigationItems = [
    { label: 'Features', href: '#features' },
    { label: 'Security', href: '#security' },
    { label: 'API', href: '#api' },
    { label: 'About', href: '#about' }
  ];

  const apiFeatures = [
    {
      icon: Code,
      title: 'RESTful API',
      description: 'Clean, intuitive REST API with comprehensive documentation and SDKs for popular languages.',
      features: ['JSON responses', 'HTTP status codes', 'Rate limiting', 'Versioning']
    },
    {
      icon: Shield,
      title: 'Secure by Default',
      description: 'Built-in encryption, authentication, and security best practices for enterprise-grade applications.',
      features: ['API key authentication', 'TLS encryption', 'Request signing', 'IP whitelisting']
    },
    {
      icon: Webhook,
      title: 'Real-time Webhooks',
      description: 'Get instant notifications for document events, notarizations, and will executions.',
      features: ['Event streaming', 'Retry logic', 'Signature verification', 'Custom endpoints']
    },
    {
      icon: Database,
      title: 'Batch Operations',
      description: 'Process thousands of documents efficiently with batch upload and bulk operations.',
      features: ['Bulk uploads', 'Async processing', 'Progress tracking', 'Error handling']
    }
  ];

  const useCases = [
    {
      title: 'Law Firm Automation',
      description: 'Automatically encrypt and notarize legal documents, wills, and contracts',
      icon: Shield,
      code: `// Upload and encrypt legal document
const result = await aegisAPI.documents.create({
  file: contractBuffer,
  metadata: {
    name: "client-contract.pdf",
    type: "legal_document",
    clientId: "client-123"
  },
  encryption: { enabled: true },
  notarization: { enabled: true }
});

// Create digital will
const will = await aegisAPI.wills.create({
  title: "Client Will",
  documentId: result.data.documentId,
  creator: {
    address: "0x123...",
    name: "John Doe"
  },
  witnesses: [{
    address: "0x456...",
    name: "Jane Smith",
    role: "primary"
  }]
});`
    },
    {
      title: 'Healthcare Records',
      description: 'HIPAA-compliant patient record management with secure sharing',
      icon: FileText,
      code: `// Secure patient record upload
const record = await aegisAPI.documents.create({
  file: patientRecordBuffer,
  metadata: {
    name: "patient-record.pdf",
    type: "medical_record",
    patientId: "patient-456"
  },
  encryption: {
    enabled: true,
    algorithm: "AES-256"
  },
  access: {
    level: "shared",
    sharedWith: [
      "doctor@hospital.com",
      "patient@email.com"
    ]
  }
});`
    },
    {
      title: 'Certificate Authority',
      description: 'Issue and verify certificates with blockchain proof',
      icon: CheckCircle,
      code: `// Batch process certificates
const certificates = await aegisAPI.documents.createBatch([
  {
    file: birthCertBuffer,
    metadata: {
      name: "birth-certificate.pdf",
      type: "birth_certificate"
    }
  },
  {
    file: marriageCertBuffer,
    metadata: {
      name: "marriage-certificate.pdf", 
      type: "marriage_certificate"
    }
  }
], {
  notarizeAll: true,
  encryptAll: true,
  webhook: "https://your-app.com/webhook"
});`
    }
  ];

  const technologyLogos = [
    {
      name: 'Bolt.new',
      description: 'AI-powered development platform',
      logo: (
        <div className="w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center">
          <img 
            src="/image copy copy.png" 
            alt="Bolt.new Logo" 
            className="w-full h-full object-contain"
          />
        </div>
      )
    },
    {
      name: 'Algorand',
      description: 'Blockchain notarization',
      logo: (
        <div className="w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center">
          <img 
            src="/image copy.png" 
            alt="Algorand Logo" 
            className="w-full h-full object-contain"
          />
        </div>
      )
    },
    {
      name: 'IPFS',
      description: 'Decentralized storage',
      logo: (
        <div className="w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center">
          <img 
            src="/image.png" 
            alt="IPFS Logo" 
            className="w-full h-full object-contain"
          />
        </div>
      )
    },
    {
      name: 'Sentry',
      description: 'Error monitoring',
      logo: (
        <div className="w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center">
          <img 
            src="/image copy copy copy.png" 
            alt="Sentry Logo" 
            className="w-full h-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
          />
        </div>
      )
    }
  ];

  const personas = [
    {
      id: 'students',
      label: 'Students & Professionals',
      icon: GraduationCap,
      title: 'Verify Your Academic & Professional Journey',
      description: 'Verify your academic transcripts, certificates, and professional licenses. Share a verifiable digital portfolio.',
      features: [
        {
          icon: BookOpen,
          title: 'Academic Transcripts',
          description: 'Securely store and verify your educational achievements with blockchain-backed authenticity.'
        },
        {
          icon: Award,
          title: 'Professional Licenses',
          description: 'Maintain tamper-proof records of certifications, licenses, and professional qualifications.'
        },
        {
          icon: Users,
          title: 'Digital Portfolio',
          description: 'Create a verifiable digital portfolio that employers and institutions can trust.'
        }
      ],
      benefits: [
        'Instant verification of credentials',
        'Tamper-proof academic records',
        'Shareable professional portfolio',
        'Global recognition and acceptance'
      ],
      testimonial: {
        quote: "Aegis Vault helped me secure my dream job by providing instantly verifiable credentials that employers could trust.",
        author: "Sarah Chen",
        role: "Software Engineer"
      }
    },
    {
      id: 'families',
      label: 'Families',
      icon: Heart,
      title: 'Protect Your Family\'s Most Important Documents',
      description: 'Securely store last wills, property deeds, and important family records. Grant access to your executor or loved ones with timed-release conditions.',
      features: [
        {
          icon: Scroll,
          title: 'Digital Wills',
          description: 'Create legally-binding digital wills with cryptographic witness verification and automated execution.'
        },
        {
          icon: Home,
          title: 'Property Records',
          description: 'Secure storage for deeds, titles, and property documents with blockchain notarization.'
        },
        {
          icon: Clock,
          title: 'Timed Access',
          description: 'Set up conditional access for family members with time-based or event-triggered release.'
        }
      ],
      benefits: [
        'Peace of mind for your family',
        'Automated will execution',
        'Secure document inheritance',
        'Legal compliance and validity'
      ],
      testimonial: {
        quote: "Knowing our family documents are secure and will be properly transferred gives us incredible peace of mind.",
        author: "Michael Rodriguez",
        role: "Family Father"
      }
    },
    {
      id: 'businesses',
      label: 'Businesses',
      icon: Building2,
      title: 'Enterprise-Grade Document Security',
      description: 'Protect intellectual property, confidential contracts, and sensitive R&D data with an immutable, time-stamped record of creation.',
      features: [
        {
          icon: Shield,
          title: 'IP Protection',
          description: 'Secure your intellectual property with blockchain timestamps proving creation and ownership.'
        },
        {
          icon: FileText,
          title: 'Contract Management',
          description: 'Store and manage confidential contracts with enterprise-grade encryption and access controls.'
        },
        {
          icon: Database,
          title: 'R&D Data Security',
          description: 'Protect sensitive research and development data with immutable audit trails.'
        }
      ],
      benefits: [
        'Legal proof of IP ownership',
        'Regulatory compliance',
        'Secure team collaboration',
        'Audit trail for compliance'
      ],
      testimonial: {
        quote: "Aegis Vault has become essential for protecting our IP and ensuring compliance across all our business operations.",
        author: "Jennifer Park",
        role: "CTO, TechCorp"
      }
    }
  ];

  const activePersonaData = personas.find(p => p.id === activePersona) || personas[0];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <motion.header
        className="fixed top-0 left-0 right-0 z-50 px-4 py-4"
        style={{ opacity: headerOpacity }}
      >
        <nav className="max-w-7xl mx-auto">
          <Card 
            glass 
            className="px-6 py-4 border border-white/20 shadow-glass backdrop-blur-xl bg-white/80"
          >
            <div className="flex items-center justify-between">
              {/* Logo */}
              <motion.div 
                className="flex items-center gap-3"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Aegis Vault</h1>
                  <p className="text-xs text-gray-500 hidden sm:block">Secure Document Management</p>
                </div>
              </motion.div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center gap-8">
                {navigationItems.map((item) => (
                  <motion.a
                    key={item.label}
                    href={item.href}
                    className="text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200"
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.2 }}
                  >
                    {item.label}
                  </motion.a>
                ))}
              </div>

              {/* Desktop Auth Buttons */}
              <div className="hidden md:flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/auth')}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Sign In
                </Button>
                <Button
                  variant="primary"
                  onClick={() => navigate('/auth')}
                  className="shadow-lg hover:shadow-xl"
                >
                  Get Started
                </Button>
              </div>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                icon={isMenuOpen ? X : Menu}
              />
            </div>
          </Card>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden mt-4 max-w-7xl mx-auto"
          >
            <Card glass className="p-6 border border-white/20 shadow-glass backdrop-blur-xl bg-white/90">
              <div className="space-y-4">
                {navigationItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="block text-gray-600 hover:text-gray-900 font-medium py-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </a>
                ))}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <Button
                    variant="ghost"
                    className="w-full justify-center"
                    onClick={() => navigate('/auth')}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="primary"
                    className="w-full justify-center"
                    onClick={() => navigate('/auth')}
                  >
                    Get Started
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </motion.header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-blue-50 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary-200/30 to-secondary-200/30 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-secondary-200/30 to-accent-200/30 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-primary-100/20 to-secondary-100/20 rounded-full blur-3xl" />
        </div>

        <motion.div 
          className="relative z-10 max-w-7xl mx-auto px-4 py-20 pt-32 text-center"
          style={{ y: heroY }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-primary-200 rounded-full text-sm font-medium text-primary-700 shadow-lg"
            >
              <CheckCircle size={16} />
              Trusted by 50,000+ users worldwide
            </motion.div>

            {/* Main Headline */}
            <div className="space-y-6">
              <h1 className="text-5xl md:text-7xl font-bold text-gray-900 leading-tight">
                Your Documents,
                <br />
                <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  Absolutely Secure
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Experience the future of document management with military-grade encryption, 
                blockchain verification, digital wills, and seamless collaboration tools.
              </p>
            </div>

            {/* CTA Button and Video */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col items-center justify-center gap-8"
            >
              <Button
                variant="primary"
                size="lg"
                icon={ArrowRight}
                iconPosition="right"
                onClick={() => navigate('/auth')}
                className="text-lg px-8 py-4 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300"
              >
                Start Free Trial
              </Button>
              
              {/* Loom Video Embed */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="w-full max-w-4xl"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-gray-900 border border-gray-200">
                  {/* Responsive iframe container */}
                  <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                    <iframe 
                      src="https://www.loom.com/embed/dd4b28600790457f82e292de22ccda04?sid=f9b10480-b185-48fc-b2fd-43b8faaa5953" 
                      frameBorder="0" 
                      allowFullScreen
                      style={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        width: '100%', 
                        height: '100%' 
                      }}
                      title="Aegis Vault Demo - Secure Document Management"
                    />
                  </div>
                </div>
                
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.0 }}
                  className="text-sm text-gray-600 mt-4 text-center max-w-2xl mx-auto"
                >
                  Watch how Aegis Vault protects your documents with enterprise-grade security, 
                  blockchain notarization, digital wills, and seamless collaboration features.
                </motion.p>
              </motion.div>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.3 + index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg">
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-gray-900">{stat.value}</div>
                  <div className="text-sm text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      </section>

      {/* Personas Section */}
      <section id="personas" className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Built for Life's Most
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent"> Important Documents</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you're protecting academic achievements, family legacies, or business assets, 
              Aegis Vault provides the security and trust you need.
            </p>
          </motion.div>

          {/* Persona Tabs */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex bg-white rounded-2xl p-2 shadow-lg border border-gray-200">
              {personas.map((persona) => (
                <button
                  key={persona.id}
                  onClick={() => setActivePersona(persona.id)}
                  className={`
                    flex items-center gap-3 px-6 py-3 rounded-xl font-medium transition-all duration-300
                    ${activePersona === persona.id
                      ? 'bg-primary-500 text-white shadow-lg'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }
                  `}
                >
                  <persona.icon size={20} />
                  <span className="hidden sm:inline">{persona.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Active Persona Content */}
          <motion.div
            key={activePersona}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-12"
          >
            {/* Hero Section for Persona */}
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center mx-auto shadow-xl">
                <activePersonaData.icon className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900">
                {activePersonaData.title}
              </h3>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {activePersonaData.description}
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {activePersonaData.features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="p-6 h-full text-center hover:shadow-xl transition-all duration-300 group">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="h-8 w-8 text-primary-600" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-3">
                      {feature.title}
                    </h4>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Benefits and Testimonial */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Benefits */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <h4 className="text-2xl font-bold text-gray-900 mb-6">
                  Why {activePersonaData.label} Choose Aegis Vault
                </h4>
                <ul className="space-y-4">
                  {activePersonaData.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Testimonial */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Card className="p-8 bg-gradient-to-br from-primary-50 to-secondary-50 border-primary-200">
                  <div className="space-y-6">
                    <div className="text-4xl text-primary-500">"</div>
                    <blockquote className="text-lg text-gray-700 italic leading-relaxed">
                      {activePersonaData.testimonial.quote}
                    </blockquote>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold">
                          {activePersonaData.testimonial.author.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {activePersonaData.testimonial.author}
                        </p>
                        <p className="text-gray-600 text-sm">
                          {activePersonaData.testimonial.role}
                        </p>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center"
            >
              <Button
                variant="primary"
                size="lg"
                icon={ArrowRight}
                iconPosition="right"
                onClick={() => navigate('/auth')}
                className="shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
              >
                Start Protecting Your Documents
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Powered by Cutting-Edge Technology Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">
              Powered by Cutting-Edge Technology
            </h2>
            
            {/* Technology Logos */}
            <div className="flex justify-center items-center gap-8 md:gap-12 mb-8">
              {technologyLogos.map((tech, index) => (
                <motion.div
                  key={tech.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex flex-col items-center gap-2 group"
                >
                  <div className="grayscale hover:grayscale-0 transition-all duration-300 transform group-hover:scale-110">
                    {tech.logo}
                  </div>
                  <span className="text-xs text-gray-600 font-medium">{tech.name}</span>
                </motion.div>
              ))}
            </div>
            
            {/* Explanation Text */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed"
            >
              We use blockchain technology for integrity, decentralized storage for resilience, 
              and end-to-end encryption for privacy.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Built for the Future of
              <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent"> Security</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Every feature is designed with security, privacy, and user experience at its core. 
              From document encryption to digital inheritance planning.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card 
                  hover 
                  className="p-8 h-full border border-gray-100 hover:border-gray-200 transition-all duration-300 group"
                >
                  <div className="space-y-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <feature.icon className="h-8 w-8 text-white" />
                    </div>
                    
                    <div className="space-y-4">
                      <h3 className="text-2xl font-bold text-gray-900 group-hover:text-primary-600 transition-colors duration-300">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>

                    <motion.div
                      className="flex items-center text-primary-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      whileHover={{ x: 5 }}
                    >
                      Learn more <ArrowRight size={16} className="ml-2" />
                    </motion.div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Will & Witness Highlight Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Scroll className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                Digital Will & Witness
              </h2>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
              Secure your digital legacy with blockchain-backed inheritance planning. 
              Create tamper-proof wills with cryptographic witness verification.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 text-center bg-white/80 backdrop-blur-sm border border-emerald-200">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <FileText className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Secure Will Creation</h3>
                <p className="text-gray-600">
                  Create legally-binding digital wills with end-to-end encryption. 
                  Your final wishes protected by military-grade security.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 text-center bg-white/80 backdrop-blur-sm border border-emerald-200">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <UserCheck className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Trusted Witnesses</h3>
                <p className="text-gray-600">
                  Establish a network of trusted witnesses with multi-signature verification. 
                  Automated wellness checks ensure proper execution.
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Card className="p-8 text-center bg-white/80 backdrop-blur-sm border border-emerald-200">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Clock className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Automated Execution</h3>
                <p className="text-gray-600">
                  Smart contracts ensure your will is executed according to your wishes. 
                  Time-based triggers and witness confirmations provide security.
                </p>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* API Section */}
      <section id="api" className="py-24 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center shadow-lg">
                <Code className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold">
                Powerful API for Developers
              </h2>
            </div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
              Integrate Aegis Vault's security features into your applications. 
              Perfect for law firms, healthcare systems, and certificate authorities.
            </p>
          </motion.div>

          {/* API Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {apiFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 bg-gray-800 border-gray-700 h-full">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-3">{feature.title}</h3>
                  <p className="text-gray-300 text-sm mb-4">{feature.description}</p>
                  <ul className="space-y-1">
                    {feature.features.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs text-gray-400">
                        <CheckCircle className="h-3 w-3 text-green-400" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Use Cases with Code Examples */}
          <div className="space-y-12">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Real-World Use Cases</h3>
              <p className="text-gray-300">See how businesses use our API to automate secure document workflows</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={useCase.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <Card className="p-6 bg-gray-800 border-gray-700 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <useCase.icon className="h-5 w-5 text-white" />
                      </div>
                      <h4 className="text-lg font-semibold text-white">{useCase.title}</h4>
                    </div>
                    <p className="text-gray-300 text-sm mb-6">{useCase.description}</p>
                    
                    <div className="bg-gray-900 rounded-lg p-4 border border-gray-600">
                      <div className="flex items-center gap-2 mb-3">
                        <Terminal className="h-4 w-4 text-green-400" />
                        <span className="text-xs text-gray-400 font-mono">JavaScript</span>
                      </div>
                      <pre className="text-xs text-gray-300 overflow-x-auto">
                        <code>{useCase.code}</code>
                      </pre>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* API Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
          >
            <div>
              <div className="text-3xl font-bold text-white mb-2">99.9%</div>
              <div className="text-gray-400 text-sm">API Uptime</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">{'<50ms'}</div>
              <div className="text-gray-400 text-sm">Response Time</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">1M+</div>
              <div className="text-gray-400 text-sm">API Calls/Month</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">3</div>
              <div className="text-gray-400 text-sm">SDKs Available</div>
            </div>
          </motion.div>

          {/* API CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <Card className="p-8 bg-gradient-to-r from-blue-600 to-purple-600 border-none">
              <h3 className="text-2xl font-bold text-white mb-4">Ready to Build?</h3>
              <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                Get started with our comprehensive API documentation and SDKs. 
                Free tier includes 1,000 API calls per month.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="secondary"
                  size="lg"
                  className="bg-white text-blue-600 hover:bg-gray-100"
                  onClick={() => navigate('/api-docs')}
                >
                  View API Docs
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  icon={ExternalLink}
                  className="border-white text-white hover:bg-white/10"
                >
                  Get API Key
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary-600 to-secondary-600 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white/10 rounded-full blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto px-4 text-center"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Secure Your Digital Life?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of users who trust Aegis Vault with their most important documents and digital legacy.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              variant="secondary"
              size="lg"
              icon={ArrowRight}
              iconPosition="right"
              onClick={() => navigate('/auth')}
              className="text-lg px-8 py-4 bg-white text-primary-600 hover:bg-gray-50 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300"
            >
              Start Your Free Trial
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="text-lg px-8 py-4 border-2 border-white text-white hover:bg-white/10"
              onClick={() => navigate('/api-docs')}
            >
              Explore API
            </Button>
          </div>

          <p className="text-white/70 text-sm mt-6">
            No credit card required • 30-day free trial • Cancel anytime
          </p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Aegis Vault</h3>
                  <p className="text-gray-400 text-sm">Secure Document Management</p>
                </div>
              </div>
              <p className="text-gray-400">
                The most secure way to store, manage, and share your important documents with digital inheritance planning.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Will & Witness</a></li>
                <li><a href="#" className="hover:text-white transition-colors">API</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Status</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Aegis Vault. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;